import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map } from 'rxjs/operators'
import { PpwLocalStorageService } from '../../app/common/services/ppwlocalstorage.service'
import {
    TimesheetProject,
    TimesheetProjectPaginatedList,
    TimesheetProjectSearchCriteria
} from '../models/timesheet-project.model'
import { BaseService } from './base.service'

@Injectable({
    providedIn: 'root'
})
export class TimesheetProjectService extends BaseService {
    get uri(): string {
        return `${this.baseUri}/TimesheetProjects`
    }

    constructor(private readonly ppwLocalStorageService: PpwLocalStorageService, readonly http: HttpClient) {
        super(http)
    }

    async getTimesheetProjects(
        timesheetProjectSearchCriteria: TimesheetProjectSearchCriteria
    ): Promise<TimesheetProjectPaginatedList> {
        const preferences = this.ppwLocalStorageService.getStoredData(
            this.ppwLocalStorageService.STORAGE_KEY_PROFILE_PREFERENCES
        )
        timesheetProjectSearchCriteria.includeArchived = preferences && preferences.showArchivedData
        timesheetProjectSearchCriteria.includeArchived = true // Marc S.: never hide logged hours in timecards nor timesheets

        return this.$http
            .get<TimesheetProjectPaginatedList>(this.uri, {
                params: timesheetProjectSearchCriteria.httpParams(),
                observe: 'response'
            })
            .pipe(
                map((response) => TimesheetProjectPaginatedList.fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async getAllTimesheetProjects(): Promise<TimesheetProjectPaginatedList> {
        const searchCriteria = new TimesheetProjectSearchCriteria(1, 100000)

        return this.getTimesheetProjects(searchCriteria)
    }

    async saveTimesheetProject(timesheetProject: TimesheetProject): Promise<TimesheetProject> {
        const uri = timesheetProject.id ? `${this.uri}/${timesheetProject.id}` : this.uri

        if (timesheetProject.id) {
            return this.$http
                .put<TimesheetProject>(uri, timesheetProject, { observe: 'response' })
                .pipe(
                    map((response) => TimesheetProject.fromJSON(this.preCheckResponseData(response))),
                    catchError((err) => this.handleError(err))
                )
                .toPromise()
        }

        return this.$http
            .post<TimesheetProject>(uri, timesheetProject, { observe: 'response' })
            .pipe(
                map((response) => TimesheetProject.fromJSON(this.preCheckResponseData(response))),
                catchError(async (err) => this.handleError(err))
            )
            .toPromise()
    }

    async archiveTimesheetProject(timesheetProject: TimesheetProject): Promise<TimesheetProject> {
        const uri = `${this.uri}/${timesheetProject.id}`

        return this.$http
            .delete<TimesheetProject>(uri, { observe: 'response' })
            .pipe(
                map((response) => TimesheetProject.fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }
}
